/**
 * VAT Number Validation Utility
 *
 * Validates EU VAT numbers (format + Belgian mod-97 checksum).
 * Shared by customer form, company settings, and any future VAT field.
 */

var VAT_PATTERNS = {
    BE: { regex: /^BE[01]\d{9}$/, checksum: true },
    FR: { regex: /^FR[A-Z0-9]{2}\d{9}$/ },
    NL: { regex: /^NL\d{9}B\d{2}$/ },
    LU: { regex: /^LU\d{8}$/ },
    DE: { regex: /^DE\d{9}$/ },
};

export function isValidVatNumber(value) {
    if (!value) return false;
    var normalized = value.toUpperCase().replace(/\s/g, '');

    for (var cc in VAT_PATTERNS) {
        var pat = VAT_PATTERNS[cc];
        if (pat.regex.test(normalized)) {
            if (pat.checksum && cc === 'BE') {
                var base = parseInt(normalized.substring(2, 10), 10);
                var check = parseInt(normalized.substring(10, 12), 10);
                return check === 97 - (base % 97);
            }
            return true;
        }
    }
    return false;
}

/**
 * Attach non-blocking VAT validation feedback to a form field.
 *
 * Behaviour:
 *   * `input`: clears any previously rendered error styling.
 *   * `blur`: if the value is non-empty and fails the JS regex/checksum,
 *     show a red border + ``.field-error`` paragraph immediately so the
 *     user notices before submitting.
 *
 * Submit is **not** intercepted: the server validates via Django's
 * ``clean_vat_number`` (shared regex + checksum lives in
 * ``apps/customers/models.py:validate_vat_number``), and the
 * ``form-error-scroll`` helper scrolls the offending field into view
 * after the HTMX swap. Mixing a client-side ``preventDefault`` with the
 * global ``data-protect-submit`` machinery in ``utils/submit-button.js``
 * caused spinner-state inconsistencies — see the team-form migration
 * (``frontend/src/pages/team-form.js``) for the canonical pattern.
 *
 * The `options.required` flag is accepted for backwards compatibility
 * but no longer changes behaviour: empty-required VAT numbers are
 * caught server-side, identically to every other required field.
 *
 * @param {HTMLElement} fieldContainer - The .field-group wrapper around the input
 * @param {HTMLInputElement} input - The VAT input element
 * @param {HTMLFormElement} form - The parent form (kept for API parity)
 * @param {string} errorMessage - Translated error message shown on blur
 * @param {Object} [options]
 * @param {boolean} [options.required] - Accepted but ignored (server-side concern)
 */
// eslint-disable-next-line no-unused-vars
export function attachVatValidation(fieldContainer, input, form, errorMessage, options) {
    if (!fieldContainer || !input) return;
    var msg = errorMessage || 'Invalid VAT number format.';

    function showError() {
        fieldContainer.classList.add('has-error');
        input.classList.add('border-red-500', 'dark:border-red-500');
        input.classList.remove('border-gray-300', 'dark:border-gray-600');
        if (!fieldContainer.querySelector('.field-error')) {
            var p = document.createElement('p');
            p.className = 'mt-1 text-sm text-red-600 dark:text-red-500 field-error';
            p.textContent = msg;
            input.parentNode.appendChild(p);
        }
    }

    function clearError() {
        fieldContainer.classList.remove('has-error');
        input.classList.remove('border-red-500', 'dark:border-red-500');
        input.classList.add('border-gray-300', 'dark:border-gray-600');
        fieldContainer.querySelectorAll('.field-error').forEach(function(el) { el.remove(); });
    }

    input.addEventListener('input', function() { clearError(); });

    input.addEventListener('blur', function() {
        var val = input.value.trim();
        if (val && !isValidVatNumber(val)) {
            showError();
        }
    });
}
